import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getOhtDoctors,
  type OhtDoctor,
  type OhtDoctorsResult,
} from "../../../../api/ohtAdmin";
import { Modal } from "../components/Modal";
import { SortCaret, useSortedRows } from "../components/useSortedRows";
import { PageOutline } from "../components/PageOutline";
import "../Overview/OverviewPage.css";
import "../Clinics/ClinicsPage.css";
import "../components/Modal.css";
import "./DoctorsPage.css";

type SortKey =
  | "name"
  | "clinicName"
  | "city"
  | "fsa"
  | "accepting"
  | "applications30d"
  | "attachments30d"
  | "pipeline"
  | "acceptedPending"
  | "declaredCapacity"
  | "usedCapacity"
  | "remainingCapacity"
  | "avgTTA"
  | "capacityUpdated";

type AcceptingFilter = "all" | "accepting" | "closed";
type CapacityFilter = "all" | "hasCapacity" | "noCapacity" | "missingDeclared" | "override";

type DoctorRow = {
  familyDoctorId: number;
  userId: number | null;
  name: string;
  gender: string;
  cpso: string;

  clinicId: number;
  clinicName: string;
  cityId: number | null;
  city: string;
  postalCode: string;
  fsa: string;

  acceptingNewPatients: boolean;
  acceptingStatusUpdatedAt: string | null;

  intakeNotes: string;
  firstAppointmentBookingUrl: string;

  declaredCapacity: number | null;
  remainingCapacityOverride: number | null;
  remainingCapacityUpdatedAt: string | null;

  usedCapacity: number;
  remainingCapacity: number | null;

  applications30d: number;
  attachments30d: number;
  pipelineCount: number;
  acceptedPendingAttachmentCount: number;

  avgTTA: number | null;
  lastAttachmentCompletedAt: string | null;
  lastRequestSubmittedAt: string | null;

  languages: string[];
  visitTypes: string[];
  coverageTypes: string[];
};

function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function fmtDays(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(1)} d`;
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtList(values: string[]): string {
  if (!values || values.length === 0) return "—";
  return values.join(", ");
}

function toDoctorRow(doctor: OhtDoctor): DoctorRow {
  return {
    familyDoctorId: doctor.familyDoctorId,
    userId: doctor.userId,
    name: doctor.fullName || `Family doctor ${doctor.familyDoctorId}`,
    gender: doctor.gender || "",
    cpso: doctor.cpso || "",

    clinicId: doctor.clinicId,
    clinicName: doctor.clinicName || "—",
    cityId: doctor.cityId,
    city: doctor.cityName || "",
    postalCode: doctor.postalCode || "",
    fsa: doctor.normalizedFsa || "",

    acceptingNewPatients: !!doctor.acceptingNewPatients,
    acceptingStatusUpdatedAt: doctor.acceptingStatusUpdatedAt,

    intakeNotes: doctor.intakeNotes || "",
    firstAppointmentBookingUrl: doctor.firstAppointmentBookingUrl || "",

    declaredCapacity: doctor.declaredCapacity,
    remainingCapacityOverride: doctor.remainingCapacityOverride,
    remainingCapacityUpdatedAt: doctor.remainingCapacityUpdatedAt,

    usedCapacity: doctor.usedCapacity || 0,
    remainingCapacity: doctor.remainingCapacity,

    applications30d: doctor.applications30d || 0,
    attachments30d: doctor.attachments30d || 0,
    pipelineCount: doctor.pipelineCount || 0,
    acceptedPendingAttachmentCount: doctor.acceptedPendingAttachmentCount || 0,

    avgTTA: doctor.avgDaysToAttachment,
    lastAttachmentCompletedAt: doctor.lastAttachmentCompletedAt,
    lastRequestSubmittedAt: doctor.lastRequestSubmittedAt,

    languages: doctor.languages || [],
    visitTypes: doctor.visitTypes || [],
    coverageTypes: doctor.coverageTypes || [],
  };
}

function acceptingLabel(row: DoctorRow): string {
  return row.acceptingNewPatients ? "Accepting" : "Closed";
}

function acceptingTone(row: DoctorRow): "yes" | "no" {
  return row.acceptingNewPatients ? "yes" : "no";
}

export default function DoctorsPage() {
  const [search, setSearch] = useState("");
  const [clinicFilter, setClinicFilter] = useState<"all" | number>("all");
  const [cityFilter, setCityFilter] = useState<string>("all");
  const [acceptingFilter, setAcceptingFilter] = useState<AcceptingFilter>("all");
  const [capacityFilter, setCapacityFilter] = useState<CapacityFilter>("all");

  const [result, setResult] = useState<OhtDoctorsResult | null>(null);
  const [rows, setRows] = useState<DoctorRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let cancel = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const live = await getOhtDoctors({}, controller.signal);
        if (cancel) return;

        setResult(live);
        setRows((live.items || []).map(toDoctorRow));
      } catch (err: any) {
        if (cancel || err?.name === "AbortError") return;
        setError(String(err?.message || "Failed to load OHT doctors."));
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    load();

    return () => {
      cancel = true;
      controller.abort();
    };
  }, []);

  const clinics = useMemo(() => {
    const map = new Map<number, string>();
    (rows || []).forEach((row) => map.set(row.clinicId, row.clinicName));
    return Array.from(map.entries())
      .map(([clinicId, clinicName]) => ({ clinicId, clinicName }))
      .sort((a, b) => a.clinicName.localeCompare(b.clinicName));
  }, [rows]);

  const cities = useMemo(() => {
    const set = new Set<string>();
    (rows || []).forEach((row) => row.city && set.add(row.city));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();

    return rows.filter((row) => {
      if (clinicFilter !== "all" && row.clinicId !== clinicFilter) return false;
      if (cityFilter !== "all" && row.city !== cityFilter) return false;

      if (acceptingFilter === "accepting" && !row.acceptingNewPatients) return false;
      if (acceptingFilter === "closed" && row.acceptingNewPatients) return false;

      if (capacityFilter === "hasCapacity" && (row.remainingCapacity == null || row.remainingCapacity <= 0)) return false;
      if (capacityFilter === "noCapacity" && (row.remainingCapacity == null || row.remainingCapacity > 0)) return false;
      if (capacityFilter === "missingDeclared" && row.declaredCapacity != null) return false;
      if (capacityFilter === "override" && row.remainingCapacityOverride == null) return false;

      if (!q) return true;

      return (
        row.name.toLowerCase().includes(q) ||
        row.clinicName.toLowerCase().includes(q) ||
        row.city.toLowerCase().includes(q) ||
        row.fsa.toLowerCase().includes(q) ||
        row.cpso.toLowerCase().includes(q)
      );
    });
  }, [rows, search, clinicFilter, cityFilter, acceptingFilter, capacityFilter]);

  const getSortValue = useCallback(
    (row: DoctorRow, key: SortKey): string | number | null => {
      switch (key) {
        case "name":
          return row.name;
        case "clinicName":
          return row.clinicName;
        case "city":
          return row.city;
        case "fsa":
          return row.fsa;
        case "accepting":
          return row.acceptingNewPatients ? 1 : 0;
        case "applications30d":
          return row.applications30d;
        case "attachments30d":
          return row.attachments30d;
        case "pipeline":
          return row.pipelineCount;
        case "acceptedPending":
          return row.acceptedPendingAttachmentCount;
        case "declaredCapacity":
          return row.declaredCapacity;
        case "usedCapacity":
          return row.usedCapacity;
        case "remainingCapacity":
          return row.remainingCapacity;
        case "avgTTA":
          return row.avgTTA;
        case "capacityUpdated":
          return row.remainingCapacityUpdatedAt ? new Date(row.remainingCapacityUpdatedAt).getTime() : null;
      }
    },
    []
  );

  const { sorted, sort, toggleSort } = useSortedRows<DoctorRow, SortKey>(
    filtered,
    getSortValue,
    { key: "name", direction: "asc" }
  );

  const filteredTotals = useMemo(() => {
    return filtered.reduce(
      (acc, row) => {
        acc.doctorCount += 1;
        acc.acceptingDoctorCount += row.acceptingNewPatients ? 1 : 0;
        acc.declaredCapacity += row.declaredCapacity || 0;
        acc.usedCapacity += row.usedCapacity || 0;
        acc.remainingCapacity += row.remainingCapacity || 0;
        acc.applications30d += row.applications30d || 0;
        acc.attachments30d += row.attachments30d || 0;
        acc.pipelineCount += row.pipelineCount || 0;
        acc.acceptedPendingAttachmentCount += row.acceptedPendingAttachmentCount || 0;
        acc.missingDeclaredCapacityCount += row.declaredCapacity == null ? 1 : 0;
        acc.overrideCount += row.remainingCapacityOverride == null ? 0 : 1;
        return acc;
      },
      {
        doctorCount: 0,
        acceptingDoctorCount: 0,
        declaredCapacity: 0,
        usedCapacity: 0,
        remainingCapacity: 0,
        applications30d: 0,
        attachments30d: 0,
        pipelineCount: 0,
        acceptedPendingAttachmentCount: 0,
        missingDeclaredCapacityCount: 0,
        overrideCount: 0,
      }
    );
  }, [filtered]);

  const selected = rows?.find((row) => row.familyDoctorId === selectedDoctorId) || null;

  return (
    <div className="oht-page">
      <header className="oht-page-header">
        <div className="oht-page-title-row">
          <div>
            <h1 className="oht-page-title">Doctors &amp; Capacity</h1>
            <p className="oht-page-subtitle">
              Doctor-level accepting status, declared capacity, remaining capacity, attachment flow, and active request pipeline.
            </p>
          </div>
        </div>
      </header>

      <section className="ocf-card oht-section">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">OHT doctor snapshot</h2>
          <span className="oht-section-hint">
            {result?.oht ? `${result.oht.name} · ${result.oht.code}` : "Live OHT scope"}
          </span>
        </div>

        <div className="oht-kpi-grid">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ocf-card ocf-kpi">
                <span className="oht-skeleton" style={{ height: 13, width: "55%" }} />
                <span className="oht-skeleton" style={{ height: 28, width: "38%", marginTop: 12 }} />
                <span className="oht-skeleton" style={{ height: 12, width: "75%", marginTop: 12 }} />
              </div>
            ))
          ) : (
            <>
              <KpiTile
                label="Doctors"
                value={fmtNumber(result?.totals.doctorCount ?? filteredTotals.doctorCount)}
                hint="Family doctors in clinics scoped to this OHT."
              />
              <KpiTile
                label="Accepting doctors"
                value={`${fmtNumber(result?.totals.acceptingDoctorCount ?? filteredTotals.acceptingDoctorCount)} / ${fmtNumber(result?.totals.doctorCount ?? filteredTotals.doctorCount)}`}
                hint="Doctors currently accepting new patient requests."
              />
              <KpiTile
                label="Remaining capacity"
                value={fmtNumber(result?.totals.remainingCapacity ?? filteredTotals.remainingCapacity)}
                hint="Computed from declared capacity, completed attachments, and override logic."
              />
              <KpiTile
                label="Active pipeline"
                value={fmtNumber(result?.totals.pipelineCount ?? filteredTotals.pipelineCount)}
                hint="Submitted, in review, waitlisted, or accepted pending attachment."
              />
            </>
          )}
        </div>
      </section>

      <section className="ocf-card oht-filters">
        <h2 className="ocf-section-title">Filters</h2>
        <div className="oht-filters-grid" style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}>
          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="oht-doc-search">
              Search
            </label>
            <input
              id="oht-doc-search"
              className="ocf-select"
              type="search"
              value={search}
              placeholder="Doctor, CPSO, clinic, city, or FSA"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="oht-doc-clinic">
              Clinic
            </label>
            <select
              id="oht-doc-clinic"
              className="ocf-select"
              value={clinicFilter === "all" ? "all" : String(clinicFilter)}
              onChange={(e) => {
                const value = e.target.value;
                setClinicFilter(value === "all" ? "all" : Number(value));
              }}
            >
              <option value="all">All clinics</option>
              {clinics.map((clinic) => (
                <option key={clinic.clinicId} value={clinic.clinicId}>
                  {clinic.clinicName}
                </option>
              ))}
            </select>
          </div>

          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="oht-doc-city">
              City
            </label>
            <select
              id="oht-doc-city"
              className="ocf-select"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
            >
              <option value="all">All cities</option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>

          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="oht-doc-accepting">
              Accepting status
            </label>
            <select
              id="oht-doc-accepting"
              className="ocf-select"
              value={acceptingFilter}
              onChange={(e) => setAcceptingFilter(e.target.value as AcceptingFilter)}
            >
              <option value="all">All statuses</option>
              <option value="accepting">Accepting only</option>
              <option value="closed">Closed only</option>
            </select>
          </div>

          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="oht-doc-capacity">
              Capacity
            </label>
            <select
              id="oht-doc-capacity"
              className="ocf-select"
              value={capacityFilter}
              onChange={(e) => setCapacityFilter(e.target.value as CapacityFilter)}
            >
              <option value="all">All capacity states</option>
              <option value="hasCapacity">Has remaining capacity</option>
              <option value="noCapacity">No remaining capacity</option>
              <option value="missingDeclared">Missing declared capacity</option>
              <option value="override">Has remaining override</option>
            </select>
          </div>
        </div>
        <p className="oht-capacity-notice">
          Capacity values are now read from the live OHT doctors endpoint. Remaining capacity is computed server-side from declared capacity, completed attachments, and remaining-capacity override logic.
        </p>
      </section>

      {error ? <div className="oht-page-error">{error}</div> : null}

      <section className="ocf-card oht-section">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">Filtered totals</h2>
          <span className="oht-section-hint">
            {loading ? "Loading…" : `${filtered.length} doctor${filtered.length === 1 ? "" : "s"} selected by filters`}
          </span>
        </div>

        <div className="oht-kpi-grid">
          <KpiTile
            label="Accepting"
            value={`${fmtNumber(filteredTotals.acceptingDoctorCount)} / ${fmtNumber(filteredTotals.doctorCount)}`}
            hint="Doctors currently marked as accepting new patient requests."
          />
          <KpiTile
            label="Declared capacity"
            value={fmtNumber(filteredTotals.declaredCapacity)}
            hint={`${fmtNumber(filteredTotals.missingDeclaredCapacityCount)} doctor(s) missing declared capacity`}
          />
          <KpiTile
            label="Remaining capacity"
            value={fmtNumber(filteredTotals.remainingCapacity)}
            hint={`${fmtNumber(filteredTotals.overrideCount)} doctor(s) using remaining-capacity override`}
          />
          <KpiTile
            label="30-day flow"
            value={`${fmtNumber(filteredTotals.attachments30d)} / ${fmtNumber(filteredTotals.applications30d)}`}
            hint="Attachments / applications in the last 30 days"
          />
        </div>
      </section>

      <section className="ocf-card oht-table-card">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">
            Doctors in OHT ({loading ? "…" : sorted.length})
          </h2>
          <span className="oht-section-hint">Click any column header to sort</span>
        </div>
        <div className="oht-table-wrap">
          <table className="oht-table oht-table--doctors">
            <colgroup>
              <col style={{ width: "15%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "7%" }} />
            </colgroup>
            <thead>
              <tr>
                <Th sortKey="name" label="Doctor" sort={sort} onSort={toggleSort} />
                <Th sortKey="clinicName" label="Clinic" sort={sort} onSort={toggleSort} />
                <Th sortKey="city" label="City" sort={sort} onSort={toggleSort} />
                <Th sortKey="fsa" label="FSA" sort={sort} onSort={toggleSort} />
                <Th sortKey="accepting" label="Accepting" sort={sort} onSort={toggleSort} />
                <Th sortKey="applications30d" label="Apps 30d" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="attachments30d" label="Attach 30d" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="pipeline" label="Pipeline" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="declaredCapacity" label="Declared" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="usedCapacity" label="Used" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="remainingCapacity" label="Remaining" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="avgTTA" label="Avg TTA" sort={sort} onSort={toggleSort} numeric />
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="oht-table-skeleton-row">
                    {Array.from({ length: 13 }).map((__, j) => (
                      <td key={j}>
                        <span
                          className="oht-skeleton"
                          style={{
                            height: "13px",
                            width: j === 0 ? "80%" : "60%",
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={13} className="oht-table-empty">
                    No doctors match your filters.
                  </td>
                </tr>
              ) : (
                sorted.map((row) => (
                  <tr key={row.familyDoctorId}>
                    <td>
                      <button
                        className="oht-table-link oht-doc-name"
                        type="button"
                        onClick={() => setSelectedDoctorId(row.familyDoctorId)}
                      >
                        {row.name}
                      </button>
                      {row.cpso ? <div className="oht-muted">CPSO {row.cpso}</div> : null}
                    </td>
                    <td>{row.clinicName}</td>
                    <td>{row.city || "—"}</td>
                    <td className="oht-table-mono">{row.fsa || "—"}</td>
                    <td>
                      <span className={`oht-doc-accept oht-doc-accept--${acceptingTone(row)}`}>
                        {row.acceptingNewPatients ? "●" : "○"} {acceptingLabel(row)}
                      </span>
                    </td>
                    <td className="oht-table-num">{fmtNumber(row.applications30d)}</td>
                    <td className="oht-table-num">{fmtNumber(row.attachments30d)}</td>
                    <td className="oht-table-num">{fmtNumber(row.pipelineCount)}</td>
                    <td className="oht-table-num">{fmtNumber(row.declaredCapacity)}</td>
                    <td className="oht-table-num">{fmtNumber(row.usedCapacity)}</td>
                    <td className="oht-table-num">{fmtNumber(row.remainingCapacity)}</td>
                    <td className="oht-table-num">{fmtDays(row.avgTTA)}</td>
                    <td className="oht-table-num">
                      <button
                        className="ocf-btn ocf-btn--secondary oht-table-action"
                        type="button"
                        onClick={() => setSelectedDoctorId(row.familyDoctorId)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <PageOutline
        whatItShows="This page shows the family doctors working within clinics in your OHT area, including who is accepting new patient requests, current capacity, recent attachment activity, active request pipeline, and average time to attachment."
        audience="For OHT Admin users who need a doctor-level view of primary care attachment capacity and patient flow across their OHT."
        updateTrigger="The page refreshes when it opens. You can use the search box and filters to quickly narrow the list by clinic, city, accepting status, or capacity status."
        dataSources={[
          {
            label: "Doctor coverage",
            status: "live",
            detail: "Shows family doctors linked to clinics within this OHT area.",
          },
          {
            label: "Accepting status",
            status: "live",
            detail: "Shows whether each doctor is accepting new patient requests or closed to new requests.",
          },
          {
            label: "Capacity picture",
            status: "live",
            detail: "Shows declared capacity, used capacity, remaining capacity, and whether a manual remaining-capacity override is being used.",
          },
          {
            label: "Patient attachment activity",
            status: "live",
            detail: "Shows recent patient requests, completed attachments, active pipeline, accepted patients pending attachment, and average time to attachment.",
          },
        ]}
        notes={[
          "Use this page to identify doctors with remaining capacity, doctors with active request flow, and doctors currently marked as accepting new patient requests.",
          "The information is intended for operational planning and follow-up, not for public ranking or performance comparison.",
          "Capacity and attachment figures should be interpreted as planning signals, not as guarantees that a doctor can accept every request.",
          "Filters only change what you see on screen; they do not change doctor profiles, clinic records, or patient requests.",
        ]}
      />

      <Modal
        open={selected != null}
        title={selected?.name || ""}
        eyebrow="Doctor detail"
        onClose={() => setSelectedDoctorId(null)}
        size="md"
      >
        {selected ? (
          <dl className="oht-drawer-kv">
            <dt>Clinic</dt>
            <dd>{selected.clinicName}</dd>
            <dt>City / FSA</dt>
            <dd>
              {selected.city || "—"} · <span className="oht-table-mono">{selected.fsa || "—"}</span>
            </dd>
            <dt>CPSO</dt>
            <dd>{selected.cpso || "—"}</dd>
            <dt>Gender</dt>
            <dd>{selected.gender || "—"}</dd>
            <dt>Accepting status</dt>
            <dd>
              <span className={`oht-doc-accept oht-doc-accept--${acceptingTone(selected)}`}>
                {selected.acceptingNewPatients ? "●" : "○"} {acceptingLabel(selected)}
              </span>
            </dd>
            <dt>Accepting status updated</dt>
            <dd>{fmtDateTime(selected.acceptingStatusUpdatedAt)}</dd>
            <dt>Declared capacity</dt>
            <dd>{fmtNumber(selected.declaredCapacity)}</dd>
            <dt>Used capacity</dt>
            <dd>{fmtNumber(selected.usedCapacity)}</dd>
            <dt>Remaining capacity</dt>
            <dd>{fmtNumber(selected.remainingCapacity)}</dd>
            <dt>Remaining capacity override</dt>
            <dd>{fmtNumber(selected.remainingCapacityOverride)}</dd>
            <dt>Capacity updated</dt>
            <dd>{fmtDateTime(selected.remainingCapacityUpdatedAt)}</dd>
            <dt>Applications in last 30 days</dt>
            <dd>{fmtNumber(selected.applications30d)}</dd>
            <dt>Attachments in last 30 days</dt>
            <dd>{fmtNumber(selected.attachments30d)}</dd>
            <dt>Active pipeline</dt>
            <dd>{fmtNumber(selected.pipelineCount)}</dd>
            <dt>Accepted pending attachment</dt>
            <dd>{fmtNumber(selected.acceptedPendingAttachmentCount)}</dd>
            <dt>Average time to attachment</dt>
            <dd>{selected.avgTTA != null ? `${selected.avgTTA.toFixed(1)} days` : "—"}</dd>
            <dt>Last request submitted</dt>
            <dd>{fmtDateTime(selected.lastRequestSubmittedAt)}</dd>
            <dt>Last attachment completed</dt>
            <dd>{fmtDateTime(selected.lastAttachmentCompletedAt)}</dd>
            <dt>Languages</dt>
            <dd>{fmtList(selected.languages)}</dd>
            <dt>Visit types</dt>
            <dd>{fmtList(selected.visitTypes)}</dd>
            <dt>Coverage types</dt>
            <dd>{fmtList(selected.coverageTypes)}</dd>
            <dt>Intake notes</dt>
            <dd>{selected.intakeNotes || "—"}</dd>
            <dt>First appointment booking URL</dt>
            <dd>{selected.firstAppointmentBookingUrl || "—"}</dd>
          </dl>
        ) : null}
      </Modal>
    </div>
  );
}

function KpiTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
}) {
  return (
    <div className="ocf-card ocf-kpi">
      <div className="ocf-kpi-label">{label}</div>
      <div className="ocf-big-number">{value}</div>
      <div className="ocf-muted">{hint}</div>
    </div>
  );
}

function Th({
  sortKey,
  label,
  sort,
  onSort,
  numeric,
}: {
  sortKey: SortKey;
  label: string;
  sort: { key: SortKey | null; direction: "asc" | "desc" };
  onSort: (key: SortKey) => void;
  numeric?: boolean;
}) {
  return (
    <th
      className={`oht-th-sortable ${numeric ? "oht-table-num" : ""}`}
      onClick={() => onSort(sortKey)}
      aria-sort={
        sort.key === sortKey
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <span className="oht-th-label">{label}</span>
      <SortCaret
        activeKey={sort.key}
        columnKey={sortKey}
        direction={sort.direction}
      />
    </th>
  );
}